import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {generatePath, Link as RouterLink, useLocation} from "react-router";
import {Box, Link, Stack, Typography} from "@mui/material";
import {sitRoutes} from "../../routing/sitRoutes";
import {CompanySettings} from "./companies/CompanySettings";
import {SitConfigForm} from "./config/SitConfigForm";
import {CurrencyTable} from "./currency/CurrencyTable";
import {LeverSettings} from "./levers/LeverSettings";
import {scrollToSettings, SETTINGS_SECTIONS} from "./settingsSections";

/** The section an older settings address opens (they all lead to this page now). By the end of the address: the
 * routes module imports this page, so its constants are not read at import time here. */
const SECTION_OF: Record<string, string> = {
    '/settings/configuration': 'general',
    '/settings/currency': 'rates',
    '/settings/levers': 'levers',
    '/settings/companies': 'companies',
};

/**
 * Savings Initiative Tracker settings on one page (the Category Profile settings pattern): a section list on the left,
 * the sections below each other. Anyone with SIT access reads them; only SIT admins change them.
 */
export const SitSettingsPage: React.FC = observer(() => {
    const {pathname, hash} = useLocation();
    const target = hash.startsWith('#settings-') ? hash.slice('#settings-'.length) : Object.entries(SECTION_OF).find(([end]) => pathname.replace(/\/$/, '').endsWith(end))?.[1];

    useEffect(() => {
        if (!target) return undefined;
        // After the sections have rendered their data
        const timer = setTimeout(() => scrollToSettings(target), 300);
        return () => clearTimeout(timer);
    }, [target]);

    return (
        <Stack direction="row" gap={3} alignItems="flex-start" sx={{p: 2}}>
            <Box component="nav" aria-label="Settings sections"
                 sx={{width: 200, flexShrink: 0, position: 'sticky', top: 16, display: {xs: 'none', md: 'block'}}}>
                <Stack gap={0.5}>
                    {SETTINGS_SECTIONS.map(s => (
                        <Link key={s.key} href={`#settings-${s.key}`} underline="hover" variant="body2" color="text.primary"
                              onClick={e => {
                                  e.preventDefault();
                                  scrollToSettings(s.key);
                              }}>
                            {s.label}
                        </Link>
                    ))}
                </Stack>
            </Box>
            <Stack gap={2} sx={{flex: 1, minWidth: 0, maxWidth: 1180}}>
                <Box>
                    <Link component={RouterLink} to={generatePath(sitRoutes.sit_initiatives)} variant="body2">
                        ‹ Savings Initiative Tracker
                    </Link>
                    <Typography component="h1" variant="h5" fontWeight={700} sx={{mt: 0.5}}>
                        Savings Initiative Tracker settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Apply to the whole tracker. Only Savings Initiative Tracker admins can change them.
                    </Typography>
                </Box>
                <div id="settings-general" className="sit-settings-anchor"/>
                <SitConfigForm/>
                <CompanySettings/>
                <div id="settings-rates" className="sit-settings-anchor"/>
                <CurrencyTable/>
                <div id="settings-levers" className="sit-settings-anchor"/>
                <LeverSettings/>
            </Stack>
        </Stack>
    );
});
